import React, { useState, useEffect, useRef, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import {
  Rocket,
  Sun,
  CloudSun,
  Moon,
  Image as ImageIcon,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { request } from "../services/networking/request";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";

//================================================================//
// 🎨 DESIGN SYSTEM & THEME
//================================================================//

const theme = {
  colors: {
    primary: "#3b82f6", // Updated to match brand
    primaryLight: "#eff6ff", // Updated to match brand
    primarySoft: "#f0f7ff", // Updated to match brand
    textHeading: "#0f172a",
    textBody: "#334155",
    textMuted: "#64748b",
    surface: "#ffffff",
    background: "#f8fafc",
    border: "#e2e8f0",
    success: "#059669",
  },
  shadows: {
    sm: "0 2px 8px rgba(15, 23, 42, 0.04)",
    md: "0 4px 16px rgba(15, 23, 42, 0.08)",
    lg: "0 8px 32px rgba(15, 23, 42, 0.12)",
  },
  radius: "16px",
};

//================================================================//
// LAYOUT & STRUCTURE
//================================================================//

const Form = styled.form`
  display: grid;
  gap: 32px;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    gap: 24px;
    margin-top: 20px;
  }
`;

const Section = styled.section`
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
  border: 1px solid #f1f5f9;
  border-radius: 16px;
  box-shadow: ${theme.shadows.md};
  padding: 28px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${theme.colors.border};
    box-shadow: ${theme.shadows.lg};
  }
  
  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 16px;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 20px;
  font-size: 1.25rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &::before {
    content: '';
    width: 4px;
    height: 24px;
    background: linear-gradient(135deg, ${theme.colors.primary} 0%, #6366f1 100%);
    border-radius: 2px;
  }
`;

const SectionTitleSimple = styled.h3`
  margin: 0 0 20px;
  font-size: 1.1rem;
  font-weight: 600;
  color: ${theme.colors.textHeading};
  letter-spacing: -0.01em;
`;
const Row = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr;
  @media (min-width: 720px) {
    grid-template-columns: ${({ cols2 }) => (cols2 ? "1fr 1fr" : "1fr")};
  }
`;

const Field = styled.div`
  display: grid;
  gap: 10px;
`;

const Label = styled.label`
  font-size: 0.95rem;
  color: ${theme.colors.textHeading};
  font-weight: 600;
  letter-spacing: -0.01em;
  
  /* Style required field asterisks */
  & > span.required {
    color: #dc2626;
    font-weight: 700;
    margin-left: 3px;
  }
`;

//================================================================//
// FORM INPUTS & CONTROLS
//================================================================//

const baseFieldStyles = `
  width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  background: #ffffff;
  color: ${theme.colors.textHeading};
  font-size: 1rem;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  transition: all 0.2s ease;
  font-family: inherit;
  
  &::placeholder { 
    color: ${theme.colors.textMuted};
    font-size: 0.95rem;
  }
  
  @media (max-width: 768px) {
    padding: 12px 14px;
    font-size: 0.95rem;
  }
  
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-2px); }
    75% { transform: translateX(2px); }
  }
`;
const Input = styled.input`
  ${baseFieldStyles}
  border: 1px solid ${props => props.$hasError ? '#ef4444' : '#f1f5f9'};
  
  ${props => props.$hasError && `
    animation: shake 0.5s ease-in-out;
  `}
  
  &:hover { 
    border-color: ${props => props.$hasError ? '#ef4444' : theme.colors.border};
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#ef4444' : theme.colors.primary};
    box-shadow: ${props => props.$hasError ? 'none' : `0 0 0 3px ${theme.colors.primaryLight}, 0 2px 8px rgba(15, 23, 42, 0.06)`};
    transform: translateY(-1px);
  }
`;
const TextArea = styled.textarea`
  ${baseFieldStyles}
  border: 1px solid ${props => props.$hasError ? '#ef4444' : '#f1f5f9'};
  min-height: 120px;
  resize: vertical;
  
  ${props => props.$hasError && `
    animation: shake 0.5s ease-in-out;
  `}
  
  &:hover { 
    border-color: ${props => props.$hasError ? '#ef4444' : theme.colors.border};
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#ef4444' : theme.colors.primary};
    box-shadow: ${props => props.$hasError ? 'none' : `0 0 0 3px ${theme.colors.primaryLight}, 0 2px 8px rgba(15, 23, 42, 0.06)`};
    transform: translateY(-1px);
  }
  
  @media (max-width: 768px) {
    min-height: 100px;
  }
`;

const Select = styled.select`
  ${baseFieldStyles}
  border: 1px solid ${props => props.$hasError ? '#ef4444' : '#f1f5f9'};
  cursor: pointer;
  
  ${props => props.$hasError && `
    animation: shake 0.5s ease-in-out;
  `}
  
  &:hover { 
    border-color: ${props => props.$hasError ? '#ef4444' : theme.colors.border};
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#ef4444' : theme.colors.primary};
    box-shadow: ${props => props.$hasError ? 'none' : `0 0 0 3px ${theme.colors.primaryLight}, 0 2px 8px rgba(15, 23, 42, 0.06)`};
    transform: translateY(-1px);
  }
  
  option {
    padding: 8px;
    background: ${theme.colors.surface};
    color: ${theme.colors.textHeading};
  }
`;

//================================================================//
// CHOICE PILLS (Task Type, Time Windows)
//================================================================//

const Pills = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
`;
const Pill = styled.button`
  text-align: left;
  padding: 14px;
  border-radius: 10px;
  font-weight: 800;
  font-size: 0.98rem;
  cursor: pointer;
  color: ${({ selected }) =>
    selected ? theme.colors.primary : theme.colors.textHeading};
  background: ${({ selected }) =>
    selected ? theme.colors.primaryLight : theme.colors.surface};
  border: 1px solid transparent;
  box-shadow: inset 0 0 0 1px
    ${({ selected }) =>
      selected ? "rgba(59,130,246,.35)" : theme.colors.border};
  display: grid;
  gap: 4px;
  small {
    font-weight: 600;
    color: ${theme.colors.textMuted};
  }
  &:hover {
    box-shadow: inset 0 0 0 1px ${theme.colors.primary};
    background: ${({ selected }) =>
      selected ? theme.colors.primaryLight : theme.colors.primarySoft};
  }
`;
const TimeWindowWrap = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;
const TimeOption = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  border-radius: 999px;
  padding: 10px 12px;
  font-weight: 800;
  font-size: 0.95rem;
  cursor: pointer;
  color: ${({ selected }) =>
    selected ? theme.colors.primary : theme.colors.textBody};
  background: ${({ selected }) =>
    selected ? theme.colors.primaryLight : theme.colors.surface};
  border: 1px solid transparent;
  box-shadow: inset 0 0 0 1px
    ${({ selected }) =>
      selected ? "rgba(59,130,246,.35)" : theme.colors.border};
  &:hover {
    background: ${({ selected }) =>
      selected ? theme.colors.primaryLight : theme.colors.primarySoft};
    box-shadow: inset 0 0 0 1px ${theme.colors.primary};
  }
`;

//================================================================//
// IMAGE UPLOADER
//================================================================//

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 ${theme.colors.primaryLight}; }
  100% { box-shadow: 0 0 0 10px rgba(59,130,246,0); }
`;
const Dropzone = styled.label`
  width: 100%;
  border-radius: ${theme.radius};
  padding: 22px;
  text-align: center;
  cursor: pointer;
  background: ${({ $dragging }) =>
    $dragging ? theme.colors.primaryLight : theme.colors.primarySoft};
  border: 1px dashed #dbe3ee;
  box-shadow: ${theme.shadows.sm};
  transition: background 0.18s, box-shadow 0.18s, border-color 0.18s;
  ${({ $dragging }) => $dragging && `animation: ${pulse} 900ms ease-out 1;`}
  .dz-title {
    margin-top: 8px;
    font-weight: 800;
    color: ${theme.colors.textHeading};
  }
  .dz-sub {
    font-size: 0.9rem;
    color: ${theme.colors.textMuted};
    margin-top: 2px;
  }
  input {
    display: none;
  }
`;
const PreviewRail = styled.div`
  position: relative;
  margin-top: 12px;
`;
const Track = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 6px 4px;
  scroll-snap-type: x mandatory;
  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #dbe3ee;
    border-radius: 999px;
  }
`;
const Thumb = styled.div`
  flex: 0 0 120px;
  height: 120px;
  scroll-snap-align: start;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: ${theme.colors.surface};
  box-shadow: ${theme.shadows.sm};
`;
const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
const RemoveBtn = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  border: none;
  background: rgba(15, 23, 42, 0.55);
  color: #fff;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: transform 0.15s;
  &:hover {
    transform: scale(1.06);
  }
`;
const StatusBadge = styled.span`
  position: absolute;
  left: 6px;
  bottom: 6px;
  font-size: 0.7rem;
  font-weight: 800;
  padding: 3px 8px;
  border-radius: 999px;
  color: #fff;
  background: ${({ status }) =>
    status === "uploaded"
      ? "#16a34a"
      : status === "error"
      ? "#dc2626"
      : "#64748b"};
  box-shadow: ${theme.shadows.sm};
`;
const ScrollBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: ${theme.colors.surface};
  box-shadow: ${theme.shadows.md};
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  cursor: pointer;
  z-index: 1;
  transition: opacity 0.2s, transform 0.2s;
  &:disabled {
    opacity: 0;
    transform: translateY(-50%) scale(0.8);
    pointer-events: none;
  }
`;
const ScrollLeft = styled(ScrollBtn)`
  left: -12px;
`;
const ScrollRight = styled(ScrollBtn)`
  right: -12px;
`;

//================================================================//
// SUBMIT BUTTON
//================================================================//

const SubmitButton = styled.button`
  color: #ffffff;
  padding: 16px 24px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: 700;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, #6366f1 100%);
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  letter-spacing: -0.01em;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(59, 130, 246, 0.4);
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    
    &::before {
      left: 100%;
    }
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;
const DesktopSubmit = styled.div`
  display: block;
  @media (max-width: 640px) {
    display: none;
  }
`;
const MobileSubmitBar = styled.div`
  display: none;
  @media (max-width: 640px) {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: saturate(180%) blur(8px);
    border-top: 1px solid ${theme.colors.border};
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom));
    display: block;
    z-index: 3;
  }
`;

//================================================================//
// 🚀 MAIN COMPONENT
//================================================================//

const MAX_FILES = 5;
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_MB = 5;

const ListingForm = ({
  formData,
  handleFormFieldChange,
  handleTimeWindowToggle,
  handleBudgetChange,
  timeWindows,
  categories,
  handleSubmit,
  handleTaskTypeChange,
  isEditMode,
  uploadMeta,
  onImagesChange,
  onImageDelete,
  validationErrors = {},
}) => {
  const [items, setItems] = useState([]);
  const [dragging, setDragging] = useState(false);
  const trackRef = useRef(null);
  const [canScroll, setCanScroll] = useState({ left: false, right: false });

  // Seed with existing images on edit
  useEffect(() => {
    if (
      isEditMode &&
      uploadMeta?.image_urls?.length > 0 &&
      items.length === 0
    ) {
      const existingImages = uploadMeta.image_urls.map((url, idx) => ({
        id: `existing-${idx}-${uploadMeta.image_keys[idx]}`,
        file: null,
        previewUrl: url,
        status: "uploaded",
        key: uploadMeta.image_keys[idx],
        publicUrl: url,
      }));
      setItems(existingImages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  // Clean up object URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      items.forEach((item) => {
        if (item.previewUrl && item.previewUrl.startsWith("blob:")) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    };
  }, [items]);

  // Update parent with uploaded keys/urls
  useEffect(() => {
    const uploaded = items.filter((item) => item.status === "uploaded");
    onImagesChange({
      image_keys: uploaded.map((item) => item.key).filter(Boolean),
      image_urls: uploaded.map((item) => item.publicUrl).filter(Boolean),
    });
  }, [items, onImagesChange]);

  // Efficiently check scroll status and update buttons
  const checkScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;

    const hasOverflow = el.scrollWidth > el.clientWidth;
    const isAtStart = el.scrollLeft === 0;
    const isAtEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 1;

    setCanScroll({
      left: hasOverflow && !isAtStart,
      right: hasOverflow && !isAtEnd,
    });
  }, []);

  useEffect(() => {
    const trackElement = trackRef.current;
    if (!trackElement) return;
    checkScroll();
    trackElement.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);
    return () => {
      trackElement.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [items, checkScroll]);

  const addFiles = async (fileList) => {
    const remainingSlots = MAX_FILES - items.length;
    if (!fileList?.length || remainingSlots <= 0) return;

    const validatedFiles = Array.from(fileList)
      .filter(
        (file) =>
          ACCEPTED_TYPES.includes(file.type) &&
          file.size <= MAX_MB * 1024 * 1024
      )
      .slice(0, remainingSlots);

    if (!validatedFiles.length) return;

    const newItems = validatedFiles.map((file) => ({
      id: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
      status: "uploading",
      key: null,
      publicUrl: null,
    }));
    setItems((prev) => [...prev, ...newItems]);

    await Promise.all(
      newItems.map(async (item) => {
        try {
          const resp = await request(
            "POST",
            API_CONSTANT_MAP.get_presigned_upload_url,
            {
              filename: item.file.name,
              content_type: item.file.type,
            }
          );
          if (!resp.data.success)
            throw new Error("Failed to get presigned URL.");

          const { presigned_url, image_url } = resp.data.data;
          const key = image_url.split(".amazonaws.com/")[1];

          await fetch(presigned_url, {
            method: "PUT",
            headers: { "Content-Type": item.file.type },
            body: item.file,
          });

          setItems((prev) =>
            prev.map((p) =>
              p.id === item.id
                ? { ...p, status: "uploaded", key, publicUrl: image_url }
                : p
            )
          );
        } catch (err) {
          console.error("Upload error:", err);
          setItems((prev) =>
            prev.map((p) => (p.id === item.id ? { ...p, status: "error" } : p))
          );
        }
      })
    );
  };

  const removeAt = (indexToRemove) => {
    const item = items[indexToRemove];
    if (item.status === "uploaded" && item.key) {
      onImageDelete(item.key);
    }

    if (item.previewUrl && item.previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(item.previewUrl);
    }

    setItems((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const scrollByPx = (px) => {
    trackRef.current?.scrollBy({ left: px, behavior: "smooth" });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Section>
        <Field>
          <Label htmlFor="title">Task Title <span className="required">*</span></Label>
          <Input
            id="title"
            value={formData.title}
            onChange={handleFormFieldChange}
            placeholder="e.g., Assemble a new desk"
            $hasError={validationErrors.title}
            required
          />
        </Field>
        <Field style={{ marginTop: "14px" }}>
          <Label htmlFor="description">Description <span className="required">*</span></Label>
          <TextArea
            id="description"
            value={formData.description}
            onChange={handleFormFieldChange}
            rows="4"
            placeholder="Describe the task in detail..."
            $hasError={validationErrors.description}
            required
          />
        </Field>
      </Section>

      <Section>
        <Field>
          <Label htmlFor="category_id">Category <span className="required">*</span></Label>
          <Select
            id="category_id"
            value={formData.category_id}
            onChange={handleFormFieldChange}
            $hasError={validationErrors.category_id}
            required
          >
            <option value="">Select a category...</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </Select>
        </Field>
      </Section>

      <Section>
        <SectionTitleSimple>Task Type</SectionTitleSimple>
        <Pills>
          <Pill
            type="button"
            selected={formData.taskType === "in-person"}
            onClick={() => handleTaskTypeChange("in-person")}
          >
            In-person <small>Tasker comes to your location</small>
          </Pill>
          <Pill
            type="button"
            selected={formData.taskType === "online"}
            onClick={() => handleTaskTypeChange("online")}
          >
            Remote <small>Task can be done remotely</small>
          </Pill>
        </Pills>

        {formData.taskType === "in-person" && (
          <Field style={{ marginTop: 14 }}>
            <Label htmlFor="zipcode">Location <span className="required">*</span></Label>
            <Input
              id="zipcode"
              value={formData.zipcode}
              onChange={handleFormFieldChange}
              placeholder="Enter ZIP Code"
              $hasError={validationErrors.zipcode}
              required
            />
          </Field>
        )}
      </Section>

      <Section>
        <Row cols2>
          <Field>
            <Label htmlFor="budget">Budget ($) <span className="required">*</span></Label>
            <Input
              id="budget"
              type="number"
              step="0.01"
              min="0"
              value={formData.budget}
              onChange={handleBudgetChange}
              placeholder="e.g., 150"
              $hasError={validationErrors.budget}
              required
            />
          </Field>
          <Field>
            <Label htmlFor="dueDate">Due Date</Label>
            <Input
              type="date"
              id="dueDate"
              value={formData.dueDate}
              onChange={handleFormFieldChange}
              min={new Date().toISOString().split("T")[0]}
            />
          </Field>
        </Row>

        <Field style={{ marginTop: 16 }}>
          <Label>Preferred Time Windows <span className="required">*</span></Label>
          <TimeWindowWrap>
            {timeWindows.map((tw) => {
              const name = tw.name.toLowerCase();
              const Icon =
                name === "morning"
                  ? Sun
                  : name === "afternoon"
                  ? CloudSun
                  : Moon;
              return (
                <TimeOption
                  key={tw.id}
                  type="button"
                  selected={formData.selectedTimeWindows.includes(tw.id)}
                  onClick={() => handleTimeWindowToggle(tw.id)}
                  title={tw.name}
                >
                  <Icon size={16} /> {tw.name}
                </TimeOption>
              );
            })}
          </TimeWindowWrap>
        </Field>
      </Section>

      <Section>
        <SectionTitleSimple>Photos (Optional)</SectionTitleSimple>
        <Dropzone
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            addFiles(e.dataTransfer.files);
          }}
          $dragging={dragging}
        >
          <ImageIcon size={40} color={theme.colors.primary} />
          <div className="dz-title">Click to browse or drag & drop</div>
          <div className="dz-sub">
            Up to {MAX_FILES} images, {MAX_MB}MB each
          </div>
          <input
            type="file"
            accept={ACCEPTED_TYPES.join(",")}
            multiple
            onChange={(e) => addFiles(e.target.files)}
            disabled={items.length >= MAX_FILES}
          />
        </Dropzone>

        {items.length > 0 && (
          <PreviewRail>
            <Track ref={trackRef}>
              {items.map((it, idx) => (
                <Thumb key={it.id}>
                  <ThumbImg src={it.previewUrl} alt="Upload preview" />
                  <RemoveBtn
                    type="button"
                    onClick={() => removeAt(idx)}
                    title="Remove"
                  >
                    <X size={14} />
                  </RemoveBtn>
                  <StatusBadge status={it.status}>
                    {it.status.charAt(0).toUpperCase() + it.status.slice(1)}
                  </StatusBadge>
                </Thumb>
              ))}
            </Track>
            <ScrollLeft
              type="button"
              onClick={() => scrollByPx(-200)}
              aria-label="Scroll left"
              disabled={!canScroll.left}
            >
              <ChevronLeft size={18} />
            </ScrollLeft>
            <ScrollRight
              type="button"
              onClick={() => scrollByPx(200)}
              aria-label="Scroll right"
              disabled={!canScroll.right}
            >
              <ChevronRight size={18} />
            </ScrollRight>
          </PreviewRail>
        )}
      </Section>

      <DesktopSubmit>
        <SubmitButton type="submit">
          <Rocket size={18} /> {isEditMode ? "Update Task" : "Publish Task"}
        </SubmitButton>
      </DesktopSubmit>

      <MobileSubmitBar>
        <SubmitButton type="submit" style={{ width: "100%" }}>
          <Rocket size={18} /> {isEditMode ? "Update Task" : "Publish Task"}
        </SubmitButton>
      </MobileSubmitBar>
    </Form>
  );
};

export default ListingForm;
